import {memo, useState, useEffect, useCallback, useMemo, MutableRefObject, Dispatch, ComponentType} from 'react';

import {useDispatch, useSelector} from 'react-redux';

import {trackEvent} from '~/shared/services/analytics';
import {createLogger} from '~/shared/logging';
import actions from '~/shared/store/actions';
import {selectUserData} from '~/shared/store/selectors';
import {CentralLoading} from '~/shared/components/Loaders';
import {LoginBody, BodyWrapper} from '~/shared/components/loginStyled';
import {onSubmitEmailHandler} from '~/shared/components/LoginOrRegister/utils/authHelpers';
import {UserCredentials, OAuthProviderCredentials} from '~/shared/store/storeModules/restrictedSharedActions';

import {LOGIN_SCREEN_TYPES, LOGIN_PROVIDER_ANALYTICS_MAP, AuthFlowStep, LoginProvider, ViewModes} from '../consts';

import LoginVerificationForm from './LoginVerificationForm';
import PasswordForm from './PasswordForm';
import LoginForm from './LoginForm';

const logger = createLogger('Login');

const LOGIN_COMPONENTS: Record<AuthFlowStep, ComponentType<any> | null> = {
  [AuthFlowStep.INITIAL]: LoginForm,
  [AuthFlowStep.OTP]: LoginVerificationForm,
  [AuthFlowStep.PASSWORD]: PasswordForm,
  [AuthFlowStep.RESET_PASSWORD_SENT]: PasswordForm,
  [AuthFlowStep.ADD_ADDRESS]: null,
};

const getAnalyticNameByScreenType = ({currentLoginScreen, verificationDetails}: {
  currentLoginScreen: string;
  verificationDetails: {authenticationToken: string; isPhoneMethod?: boolean} | null;
}) => {
  const loginScreenAnalyticsNames = {
    [LOGIN_SCREEN_TYPES.email]: '2FA - Enter Email',
    [LOGIN_SCREEN_TYPES.password]: '2FA - B2B FTO',
    [LOGIN_SCREEN_TYPES.verification]: verificationDetails?.isPhoneMethod ? '2FA - SMS' : '2FA - Email',
  };

  return loginScreenAnalyticsNames[currentLoginScreen];
};

type LoginProps = {
  backgroundColor?: string;
  onBackButtonClick: () => void;
  pageView: boolean;
  scrollModalToBottom?: () => void;
  onSubmit: (values: UserCredentials & {userToken?: string}, linkType: string | null, providerFromArgs: LoginProvider) => Promise<void>;
  email: string | null;
  setEmail: Dispatch<string | null>;
  isStrongLogin: boolean;
  openProviderScreen?: (options: {
    providerProps: Record<string, string | number | undefined>;
    nextLoginProvider: LoginProvider;
    nextScreen: ViewModes;
  }) => void;
  isRedirectedToStepRef: MutableRefObject<boolean>;
  verificationDetails: {authenticationToken: string} | null;
  setVerificationDetails: Dispatch<{authenticationToken: string} | null>;
  fromCheckout?: boolean;
  loginProvider: LoginProvider;
  authFlowStep: AuthFlowStep;
  setAuthFlowStep: Dispatch<AuthFlowStep>;
} & OAuthProviderCredentials;

const Login = ({
  backgroundColor,
  onBackButtonClick,
  pageView,
  scrollModalToBottom,
  onSubmit,
  email,
  setEmail,
  isStrongLogin,
  openProviderScreen,
  isRedirectedToStepRef,
  verificationDetails,
  setVerificationDetails,
  fromCheckout,
  loginProvider,
  authFlowStep,
  setAuthFlowStep,
  facebookUserAccessToken,
  facebookUserId,
  googleUserId,
  googleCode,
  ...props
}: LoginProps) => {
  const [currentLoginScreen, setCurrentLoginScreen] = useState(LOGIN_SCREEN_TYPES.email);
  const [resErrors, setResErrors] = useState<string | null>(null);
  const dispatch = useDispatch();
  const userData = useSelector(selectUserData);

  const isStrongLoginAutoSubmitting = useMemo(() => {
    return isStrongLogin && !!userData?.email && currentLoginScreen === LOGIN_SCREEN_TYPES.email;
  }, [currentLoginScreen, isStrongLogin, userData?.email]);

  const PresentedComponent = LOGIN_COMPONENTS[authFlowStep];

  useEffect(() => {
    setResErrors(null);
    if (!isStrongLogin && !userData) {
      dispatch(actions.clearUserWithLoaded());
    }
  }, [currentLoginScreen, dispatch, isStrongLogin, userData]);

  const linkType = getAnalyticNameByScreenType({currentLoginScreen, verificationDetails});

  const onSubmitEmail = useCallback(
    async ({email: emailInput, shouldSendTrackEvent = true}: {
      email: string;
      shouldSendTrackEvent?: boolean;
    }) => {
      await onSubmitEmailHandler({
        email: emailInput,
        setEmail,
        setResErrors,
        shouldSendTrackEvent,
        linkType,
        setVerificationDetails,
        setCurrentLoginScreen,
        fromCheckout,
        loginProvider,
        setAuthFlowStep,
      });
    },
    [fromCheckout, setEmail, setVerificationDetails, loginProvider, setAuthFlowStep, linkType]);

  useEffect(() => {
    if (isStrongLoginAutoSubmitting) {
      onSubmitEmail({email: userData?.email || '', shouldSendTrackEvent: false});
    }
  }, [isStrongLoginAutoSubmitting, onSubmitEmail, userData?.email]);

  const onSubmitVerification = async (entries: {authenticationCode?: string}) => {
    const {authenticationCode} = entries;

    if (!authenticationCode || !email) {
      logger.error('onSubmitVerification didnt receive an authenticationCode');
      return;
    }
    trackEvent('loginOTP_submit', {onCheckout: fromCheckout, authenticationProvider: LOGIN_PROVIDER_ANALYTICS_MAP[loginProvider]});
    trackEvent('hasClickedLogin', {linkType});

    trackEvent('hasClickedLogin', {linkType});
    setResErrors(null);
    if (!isStrongLogin) {
      dispatch(actions.clearUserWithLoaded());
    }
    await onSubmit({
      email,
      facebookUserAccessToken,
      facebookUserId,
      authenticationCode,
      authenticationToken: verificationDetails?.authenticationToken,
      googleCode,
      googleUserId,
    }, linkType, loginProvider);
  };

  const onSubmitLoginWithEmailAndPassword = async ({password, ...rest}: Partial<UserCredentials & {userToken?: string; password?: string}>) => {
    trackEvent('hasClickedLogin', {linkType});
    await onSubmit({email: email || '', password, ...rest}, linkType, loginProvider);
  };

  const onForgotPassword = () => {
    trackEvent('hasClickedForgotPassword', {linkType});
    dispatch(actions.resetPassword({emailAddress: email}));
    setAuthFlowStep(AuthFlowStep.RESET_PASSWORD_SENT);
  };

  const onBackClick = () => {
    setCurrentLoginScreen(LOGIN_SCREEN_TYPES.email);
    setAuthFlowStep(AuthFlowStep.INITIAL);
  };

  useEffect(() => {
    trackEvent('hasViewedLoginStep', {linkType});
  }, [linkType]);

  useEffect(() => {
    if (scrollModalToBottom) {
      window.addEventListener('resize', scrollModalToBottom);
    }

    return () => {
      if (scrollModalToBottom) {
        window.removeEventListener('resize', scrollModalToBottom);
      }
    };
  }, [scrollModalToBottom]);

  if (!PresentedComponent) {
    return null;
  }

  return (
    <>
      {isStrongLoginAutoSubmitting && !resErrors && <CentralLoading />}
      <BodyWrapper
        id="login_tab_controls"
        aria-labelledby="login_tab_button"
        role="tabpanel"
        pageView={pageView}
        backgroundColor={backgroundColor}
      >
        <LoginBody
          verificationMode={authFlowStep === AuthFlowStep.OTP}
          isResetPasswordView={authFlowStep === AuthFlowStep.RESET_PASSWORD_SENT}
        >
          <PresentedComponent
            {...{
              onBackButtonClick,
              backgroundColor,
              onSubmitEmail,
              verificationDetails,
              onSubmitVerification,
              email,
              resErrors,
              setResErrors,
              onForgotPassword,
              onBackClick,
              onSubmitLoginWithEmailAndPassword,
              initialValues: {email},
              pageView,
              isStrongLogin,
              openProviderScreen,
              isRedirectedToStepRef,
              fromCheckout,
              loginProvider,
              ...props,
            }}
          />
        </LoginBody>
      </BodyWrapper>
    </>
  );
};

export default memo(Login);
